import { Fragment, useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useDispatch, useSelector } from "react-redux";
import AISocialIcon from "../icons/AISocialIcon";
import LayoutAction from "../actions/LayoutAction";

const NOTIFICATION_TTL_MS = 5000;

export default function NotificationPopup() {
  const dispatch = useDispatch();

  const [show, setShow] = useState(false);

  const notification = useSelector((state: any) => state.notification.getIn(["notification"]));
  const type = notification.get("type");
  const variant = notification.get("variant");
  const isAIGenerating = variant === "aiGenerating";

  useEffect(() => {
    const title = notification.get("title");
    if (!title) {
      setShow(false);
      return;
    }
    const timestamp = notification.get("timestamp");
    if (timestamp && Date.now() - timestamp > NOTIFICATION_TTL_MS) return;

    setShow(true);
    const duration = Number(notification.get("duration")) || 3000;
    const timer = setTimeout(() => {
      setShow(false);
      dispatch(LayoutAction.clearNotification());
    }, duration);
    return () => clearTimeout(timer);
  }, [dispatch, notification]);

  const renderIcon = () => {
    if (isAIGenerating) {
      return (
        <div className="h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center">
          <AISocialIcon size={16} color="#B45309" />
        </div>
      );
    }
    if (type == "success") {
      return <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />;
    }
    return <ExclamationCircleIcon className="h-6 w-6 text-red-400" aria-hidden="true" />;
  };

  return (
    <>
      <div
        aria-live="assertive"
        className="z-50 pointer-events-none fixed inset-0 flex items-end px-6 py-8 sm:items-start sm:p-16"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          <Transition
            show={show}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg shadow-lg ring-1 ${
                isAIGenerating
                  ? "bg-yellow-50 ring-yellow-200"
                  : "bg-white ring-black ring-opacity-5"
              }`}
            >
              <div className="p-4">
                <div className="flex items-start">
                  <div className="shrink-0">{renderIcon()}</div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p
                      className={`text-sm font-semibold ${
                        isAIGenerating ? "text-yellow-900" : "text-gray-900"
                      }`}
                    >
                      {notification.get("title")}
                    </p>
                    <p
                      className={`mt-1 text-sm ${
                        isAIGenerating ? "text-yellow-900" : "text-gray-500"
                      }`}
                    >
                      {notification.get("message")}
                    </p>
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <button
                      type="button"
                      className={`inline-flex rounded-md ${
                        isAIGenerating
                          ? "bg-yellow-50 text-yellow-700 hover:text-yellow-800"
                          : "bg-white text-gray-400 hover:text-gray-500"
                      } focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                      onClick={() => {
                        setShow(false);
                      }}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}
